import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';

/**
 * What happened to these goods, and what their value should do.
 *
 * The old screen showed every return as a pending refund. That described an
 * obligation to pay money out that nobody had agreed to and, for goods the
 * customer had not paid for, did not exist. This screen shows the trail
 * instead -- who raised the order, whether it was invoiced, paid, delivered
 * and received -- and then what settling the return would do to the balance,
 * before anybody agrees to it.
 */

const naira = (n) =>
  Number(n || 0).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });

const STEP_TONE = {
  'Order raised': '#2D6CDF',
  'Invoiced': '#6B4FBB',
  'Payment received': '#1B8A5A',
  'Dispatched': '#B26B00',
  'Received by customer': '#1B8A5A',
  'Signed for': '#1B8A5A',
  'Returned': '#C62828',
  'Credited': '#1B8A5A',
  'Credit reversed': '#C62828',
};

export default function ReturnTrail({ row, onClose, onChanged, notify }) {
  const [trail, setTrail] = useState(null);
  const [plan, setPlan] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const say = useCallback((msg, kind) => {
    if (notify) notify(msg, kind);
  }, [notify]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await authedFetch(`/api/returns/${row.id}/trail`);
      if (!r.ok) throw new Error('Could not load the trail.');
      const data = await r.json();
      setTrail(data);

      // The plan is only meaningful once the return is attached to a sale.
      if (data.settlement_status !== 'needs_review') { setPlan(null); return; }
      if (!data.order_number) { setPlan(null); return; }
      const p = await authedFetch(`/api/returns/${row.id}/settlement-preview`);
      if (p.ok) setPlan(await p.json());
    } catch (e) {
      setError(e.message);
    }
  }, [row.id]);

  useEffect(() => { load(); }, [load]);

  const findLines = useCallback(async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams({ product_id: row.product_id });
      if (search.trim()) qs.set('search', search.trim());
      const r = await authedFetch(`/api/returns/order-lines?${qs}`);
      if (!r.ok) throw new Error('Could not search orders.');
      const rows = await r.json();
      setCandidates(rows);
      if (!rows.length) say('No orders found for this product.', 'info');
    } catch (e) {
      say(e.message, 'error');
    } finally {
      setBusy(false);
    }
  }, [row.product_id, search, say]);

  const act = async (url, body, okMsg) => {
    setBusy(true);
    try {
      const r = await authedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'That did not work.');
      say(okMsg, 'success');
      setCandidates([]);
      await load();
      if (onChanged) onChanged();
      return data;
    } catch (e) {
      say(e.message, 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const linked = trail && trail.order_number;
  const settled = trail && trail.settlement_status !== 'needs_review';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 820, width: '94%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>
            {row.product_name} &mdash; {Number(row.quantity).toLocaleString()} returned
          </h3>
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        </div>

        <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
          {error && (
            <div style={{ background: '#FDECEA', color: '#B3261E', padding: 12, borderRadius: 8 }}>
              {error}
            </div>
          )}

          {trail && (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 10, marginBottom: 16,
              }}>
                <Fact label="Customer" value={trail.customer_name || 'Not linked'} />
                <Fact label="Order" value={trail.order_number || 'Not linked'} />
                <Fact label="Order raised by" value={trail.order_created_by || '—'} />
                <Fact label="Return taken by" value={trail.recorded_by} />
                <Fact
                  label="Value"
                  value={trail.return_value == null ? 'Not valued' : naira(trail.return_value)}
                />
                <Fact label="Settlement" value={trail.settlement_status.replace(/_/g, ' ')} />
              </div>

              {trail.note && (
                <div style={{
                  background: '#FFF4E5', color: '#8A5300', padding: 12,
                  borderRadius: 8, marginBottom: 16, fontSize: 13,
                }}>
                  {trail.note}
                </div>
              )}

              <h4 style={{ margin: '0 0 10px' }}>What happened</h4>
              <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
                {trail.steps.map((s, i) => (
                  <li key={i} style={{
                    borderLeft: `3px solid ${STEP_TONE[s.step] || '#9AA0A6'}`,
                    padding: '6px 0 10px 12px', marginBottom: 2,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {s.step}
                      <span style={{ fontWeight: 400, color: '#5F6368', marginLeft: 8 }}>
                        {s.at ? new Date(s.at).toLocaleString('en-GB') : 'date not recorded'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: '#3C4043' }}>{s.detail}</div>
                    {s.who && (
                      <div style={{ fontSize: 11.5, color: '#5F6368' }}>by {s.who}</div>
                    )}
                  </li>
                ))}
              </ol>
            </>
          )}

          {/* ---- matching an unlinked return to the sale it came from ---- */}
          {trail && !linked && (
            <div style={{ borderTop: '1px solid #E3E6EA', paddingTop: 16 }}>
              <h4 style={{ margin: '0 0 6px' }}>Match this return to the sale</h4>
              <p style={{ fontSize: 12.5, color: '#5F6368', margin: '0 0 10px' }}>
                Until it is matched there is no customer to credit and no price
                to credit them at. Only orders containing this product are shown.
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Order or invoice number (optional)"
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #CDD2D7' }}
                />
                <button className="btn btn-primary" disabled={busy} onClick={findLines}>
                  {busy ? 'Searching…' : 'Find orders'}
                </button>
              </div>

              {candidates.map((c) => (
                <div key={c.sales_order_line_id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  border: '1px solid #E3E6EA', borderRadius: 8, padding: 10, marginBottom: 6,
                }}>
                  <div style={{ fontSize: 12.5 }}>
                    <strong>{c.order_number}</strong>
                    {c.invoice_number ? ` · ${c.invoice_number}` : ''}
                    <div style={{ color: '#5F6368' }}>
                      {c.customer_name} &middot; sold {c.quantity_sold.toLocaleString()} @ {naira(c.unit_price)}
                      {c.already_returned > 0 &&
                        ` · ${c.already_returned.toLocaleString()} already back`}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={busy || c.quantity_available_to_return < Number(row.quantity)}
                    title={c.quantity_available_to_return < Number(row.quantity)
                      ? `Only ${c.quantity_available_to_return} of this line is still outstanding`
                      : 'Attach this return to this order line'}
                    onClick={() => act(
                      `/api/returns/${row.id}/link`,
                      { sales_order_line_id: c.sales_order_line_id },
                      `Matched to ${c.order_number}.`,
                    )}
                  >
                    Match
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ---- what settling would do ---------------------------------- */}
          {plan && !settled && linked && (
            <div style={{ borderTop: '1px solid #E3E6EA', paddingTop: 16 }}>
              <h4 style={{ margin: '0 0 6px' }}>What settling this will do</h4>
              <p style={{ fontSize: 12.5, color: '#5F6368', margin: '0 0 10px' }}>
                The value clears what the customer owes, oldest debt last.
                Nothing is paid out here.
              </p>
              <table className="data-table" style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th>Order</th><th>Owed before</th><th>Credited</th><th>Owed after</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.allocations.map((a) => (
                    <tr key={a.sales_order_id}>
                      <td>
                        {a.order_number}
                        {a.is_originating_order && (
                          <span style={{ fontSize: 10.5, color: '#5F6368' }}> (this sale)</span>
                        )}
                      </td>
                      <td>{naira(a.outstanding_before)}</td>
                      <td style={{ color: '#1B8A5A', fontWeight: 600 }}>{naira(a.amount)}</td>
                      <td>{naira(a.outstanding_after)}</td>
                    </tr>
                  ))}
                  {!plan.allocations.length && (
                    <tr>
                      <td colSpan={4} style={{ color: '#5F6368' }}>
                        This customer owes nothing, so none of it clears a debt.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {plan.credit_on_account > 0 && (
                <div style={{
                  background: '#E8F0FE', color: '#174EA6', padding: 12,
                  borderRadius: 8, margin: '12px 0', fontSize: 12.5,
                }}>
                  {naira(plan.credit_on_account)} has nowhere to go against a debt
                  and will be held as credit on this customer&rsquo;s account.
                  Paying it out in cash is a separate decision an admin has to
                  approve with a reason.
                </div>
              )}

              <button
                className="btn btn-primary"
                disabled={busy}
                onClick={() => act(`/api/returns/${row.id}/settle`, {}, 'Return settled.')}
              >
                {busy ? 'Settling…' : 'Settle this return'}
              </button>
            </div>
          )}

          {settled && (
            <div style={{
              background: '#E6F4EA', color: '#137333', padding: 12,
              borderRadius: 8, fontSize: 12.5,
            }}>
              Settled. The credits above are part of the record and cannot be
              edited &mdash; withdrawing one is a reversal, which stays visible.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div style={{ background: '#F6F8FA', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ fontSize: 10.5, color: '#5F6368', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#202124' }}>{value}</div>
    </div>
  );
}
